// Governance knowledge base for Asociación Condominio Costa Dorada I.
//
// The association's operative rules, distilled from its governing documents so
// the board and administrator can act correctly and legally protected WITHOUT
// re-reading ~250 pages of PDFs. This is the app's "rules-as-data" layer:
// hand-curated, bilingual, and CITED back to the source document/article.
//
// Sources:
//   ESCRITURA      — Escritura Matriz (master deed, Ley 129-2020 régimen de propiedad horizontal)
//   REGLAMENTO     — Reglamento (bylaws)
//   MANUAL         — Manual de Normas de Seguridad, Convivencia y Arquitectura
//   EMERGENCIA     — Plan de Manejo de Emergencias y Desastres
//   RACIONAMIENTO  — Plan de Racionamiento de Agua y Energía
//
// The PDFs remain the authoritative source — every rule links to its citation.
// Fines/late-payment values are surfaced as GUIDANCE (not auto-enforced).

export type RuleSource = "ESCRITURA" | "REGLAMENTO" | "MANUAL" | "EMERGENCIA" | "RACIONAMIENTO";

export type RuleTag =
  | "works-permit"
  | "architectural"
  | "fine"
  | "late-payment"
  | "rental"
  | "pet"
  | "parking"
  | "access"
  | "trash"
  | "meeting"
  | "quorum"
  | "vote"
  | "reserve"
  | "transfer-fee"
  | "incident"
  | "emergency"
  | "rationing";

export interface GovernanceRule {
  id: string;
  source: RuleSource;
  /** Human-readable citation, e.g. "Reglamento Art. VI Sec. 2(T)", "Manual §7.2". */
  citation: string;
  tags: RuleTag[];
  titleEs: string;
  titleEn: string;
  /** The operative rule in plain language. */
  summaryEs: string;
  summaryEn: string;
  /** ES + EN synonyms / colloquial terms for the matcher. Lowercase. */
  keywords: string[];
}

export const GOVERNANCE_RULES: GovernanceRule[] = [
  // ============ FINES & LATE PAYMENT ============
  {
    id: "fine-ladder",
    source: "REGLAMENTO",
    citation: "Reglamento Art. VI Sec. 2(T)",
    tags: ["fine", "incident"],
    titleEs: "Escala de multas por violación al Reglamento",
    titleEn: "Fine schedule for rule violations",
    summaryEs:
      "Ante una violación al Reglamento o al Manual de Normas: 1ª vez, aviso escrito; 2ª vez, multa de $50; 3ª vez, multa de $100; y de continuar, $100 por cada día que persista la violación. El titular tiene derecho a vista/aclaración y las multas se cargan a su cuenta de mantenimiento.",
    summaryEn:
      "For a violation of the Reglamento or Manual de Normas: 1st time, written notice; 2nd time, $50 fine; 3rd time, $100 fine; and if it continues, $100 for each day the violation persists. The owner has a right to a hearing and fines are billed to the maintenance account.",
    keywords: [
      "multa", "multas", "penalidad", "sancion", "violacion", "infraccion", "50", "100",
      "fine", "fines", "penalty", "violation", "sanction", "escala",
    ],
  },
  {
    id: "fine-architectural",
    source: "MANUAL",
    citation: "Manual §6.8",
    tags: ["fine", "architectural", "works-permit"],
    titleEs: "Multa por obra sin permiso o fuera de norma",
    titleEn: "Fine for unpermitted or non-conforming work",
    summaryEs:
      "Realizar una obra o modificación sin permiso, o que no cumpla con las resoluciones arquitectónicas, conlleva una multa automática de $100 más $100 por cada mes que persista, más un recargo de 10% sobre lo adeudado. La Junta puede exigir la restitución al estado original a costo del titular.",
    summaryEn:
      "Doing work or a modification without a permit, or not conforming to the architectural resolutions, carries an automatic $100 fine plus $100 for each month it persists, plus a 10% surcharge on the balance owed. The board may require restoration to the original state at the owner's cost.",
    keywords: [
      "multa obra", "sin permiso", "arquitectonica", "modificacion", "restituir", "restauracion",
      "unpermitted", "fine", "architectural", "restore", "penalty",
    ],
  },
  {
    id: "fine-trash",
    source: "MANUAL",
    citation: "Manual §2.3",
    tags: ["fine", "trash"],
    titleEs: "Multa por manejo indebido de basura o escombros",
    titleEn: "Fine for improper trash or debris disposal",
    summaryEs:
      "Depositar basura fuera de horario/lugar, o dejar enseres, muebles o escombros de construcción en áreas comunes, conlleva multa de $100 por día a partir del segundo día. Los escombros de obra son responsabilidad del titular y su contratista.",
    summaryEn:
      "Depositing trash outside the allowed time/place, or leaving appliances, furniture, or construction debris in common areas, carries a $100/day fine starting the second day. Construction debris is the owner's and contractor's responsibility.",
    keywords: [
      "basura", "escombros", "enseres", "muebles", "desperdicios", "trash", "garbage", "debris",
      "appliances", "furniture", "multa basura",
    ],
  },
  {
    id: "late-fee",
    source: "REGLAMENTO",
    citation: "Reglamento Art. IX (cuota 418A)",
    tags: ["late-payment", "fine"],
    titleEs: "Recargo por atraso en la cuota de mantenimiento",
    titleEn: "Late charge on the maintenance fee",
    summaryEs:
      "La cuota vence el 1º de cada mes. Pasados 30 días sin pago se aplica un recargo de 10% (cuenta 418A) más intereses. Si el atraso supera 3 meses consecutivos, se añade 1% mensual adicional sobre el balance.",
    summaryEn:
      "The fee is due on the 1st of each month. After 30 days unpaid, a 10% late charge applies (account 418A) plus interest. If the delinquency exceeds 3 consecutive months, an additional 1% per month is added to the balance.",
    keywords: [
      "recargo", "atraso", "morosidad", "cuota vencida", "10", "interes", "418a",
      "late", "overdue", "delinquent", "surcharge", "past due",
    ],
  },
  {
    id: "collections",
    source: "REGLAMENTO",
    citation: "Reglamento Art. IX",
    tags: ["late-payment"],
    titleEs: "Cobro de morosidad y corte de servicios",
    titleEn: "Delinquency collection and service cutoff",
    summaryEs:
      "Antes de acción judicial se envía requerimiento por correo certificado con 15 días para saldar. Tras 2 meses de atraso la Junta puede suspender servicios y accesos (AVI/beeper) hasta ponerse al día. La deuda de mantenimiento tiene gravamen preferente sobre el apartamento.",
    summaryEn:
      "Before judicial action, a certified-mail demand gives 15 days to cure. After 2 months delinquent the board may suspend services and access (AVI/beeper) until brought current. The maintenance debt is a priority lien on the apartment.",
    keywords: [
      "cobro", "morosidad", "requerimiento", "correo certificado", "corte de servicio", "gravamen",
      "collection", "demand", "lien", "cutoff", "judicial", "cobro judicial",
    ],
  },

  // ============ WORKS PERMITS ============
  {
    id: "works-permit-process",
    source: "MANUAL",
    citation: "Manual §6.3",
    tags: ["works-permit"],
    titleEs: "Cómo solicitar un permiso de obra a la Junta",
    titleEn: "How to request a works permit from the board",
    summaryEs:
      "Toda obra, remodelación o modificación requiere solicitud escrita a la Junta ANTES de comenzar, con: descripción del trabajo, planos/especificaciones, materiales, contratista, fechas de inicio y fin, y consentimiento del vecino cuando afecte elementos comunes limitados. No se puede iniciar sin aprobación por escrito.",
    summaryEn:
      "Any work, remodel, or modification requires a written request to the board BEFORE starting, including: description of the work, plans/specs, materials, contractor, start and end dates, and neighbor consent when limited common elements are affected. Work cannot begin without written approval.",
    keywords: [
      "permiso", "obra", "remodelacion", "remodelar", "construccion", "renovacion", "solicitud",
      "permit", "works", "remodel", "construction", "renovation", "modificacion", "planos",
    ],
  },
  {
    id: "works-permit-bond",
    source: "MANUAL",
    citation: "Manual §6.4",
    tags: ["works-permit", "fine"],
    titleEs: "Fianza (depósito) de obra: $1,000",
    titleEn: "Works bond (deposit): $1,000",
    summaryEs:
      "Antes de comenzar una obra el titular deposita una fianza de $1,000 que responde por daños a áreas comunes, limpieza y disposición de escombros. Se devuelve al finalizar si no hubo daños ni incumplimientos.",
    summaryEn:
      "Before starting work the owner posts a $1,000 bond covering damage to common areas, cleanup, and debris disposal. It is refunded on completion if there was no damage or noncompliance.",
    keywords: [
      "fianza", "deposito", "1000", "garantia", "bond", "deposit", "obra", "danos",
    ],
  },
  {
    id: "construction-hours",
    source: "MANUAL",
    citation: "Manual §6.6",
    tags: ["works-permit"],
    titleEs: "Horario permitido de construcción",
    titleEn: "Permitted construction hours",
    summaryEs:
      "Trabajos de construcción: lunes a viernes de 8:00 a.m. a 5:00 p.m.; sábados solo trabajo liviano y sin ruido; prohibido domingos y días feriados. Fuera de este horario constituye violación sujeta a multa.",
    summaryEn:
      "Construction work: Monday–Friday 8:00 a.m.–5:00 p.m.; Saturdays light, quiet work only; prohibited on Sundays and holidays. Working outside these hours is a violation subject to a fine.",
    keywords: [
      "horario", "construccion", "obra", "ruido", "sabado", "domingo", "feriado",
      "hours", "construction", "noise", "schedule", "saturday", "sunday", "holiday",
    ],
  },
  {
    id: "works-permit-appeal",
    source: "MANUAL",
    citation: "Manual §6.7",
    tags: ["works-permit"],
    titleEs: "Reconsideración y apelación de un permiso denegado",
    titleEn: "Reconsideration and appeal of a denied permit",
    summaryEs:
      "Si la Junta deniega un permiso, el titular tiene 20 días para pedir reconsideración por escrito. De mantenerse la negativa, puede llevar el asunto a asamblea extraordinaria del Consejo de Titulares.",
    summaryEn:
      "If the board denies a permit, the owner has 20 days to request reconsideration in writing. If the denial stands, the owner may bring the matter to an extraordinary assembly of the Consejo de Titulares.",
    keywords: [
      "reconsideracion", "apelacion", "denegado", "20 dias", "asamblea extraordinaria",
      "reconsideration", "appeal", "denied", "extraordinary assembly",
    ],
  },

  // ============ ARCHITECTURAL RESOLUTIONS (Manual §7) ============
  {
    id: "arch-rejas",
    source: "MANUAL",
    citation: "Manual §7 (Rejas)",
    tags: ["architectural", "works-permit"],
    titleEs: "Rejas de seguridad",
    titleEn: "Security bars (rejas)",
    summaryEs:
      "Las rejas deben ser color blanco, con barras de 1 pulgada separadas 4 pulgadas, instaladas por dentro y sin sobresalir de la fachada. Requieren permiso de obra aprobado.",
    summaryEn:
      "Security bars must be white, with 1-inch bars spaced 4 inches apart, installed on the inside and not protruding from the façade. An approved works permit is required.",
    keywords: ["rejas", "reja", "barras", "seguridad", "bars", "security bars", "grille", "blanco"],
  },
  {
    id: "arch-awnings",
    source: "MANUAL",
    citation: "Manual §7 (Toldos)",
    tags: ["architectural", "works-permit"],
    titleEs: "Toldos",
    titleEn: "Awnings (toldos)",
    summaryEs:
      "Los toldos deben ser blancos, retractables, con proyección máxima de 7 pies, y uniformes con la fachada. Requieren permiso de obra aprobado.",
    summaryEn:
      "Awnings must be white, retractable, with a maximum 7-foot projection, and uniform with the façade. An approved works permit is required.",
    keywords: ["toldo", "toldos", "marquesina", "awning", "awnings", "retractable", "blanco"],
  },
  {
    id: "arch-ac",
    source: "MANUAL",
    citation: "Manual §7 (Acondicionadores de aire)",
    tags: ["architectural", "works-permit"],
    titleEs: "Acondicionadores de aire (mini-split)",
    titleEn: "Air conditioning (mini-split)",
    summaryEs:
      "Las unidades de aire tipo mini-split deben instalarse en la ubicación aprobada (sin afectar la fachada frontal ni el paso peatonal), con condensado canalizado. La condensadora no puede colocarse donde gotee o moleste al vecino. Requiere permiso.",
    summaryEn:
      "Mini-split A/C units must be installed in the approved location (not affecting the front façade or pedestrian walkway), with condensate piped away. The condenser cannot be placed where it drips on or disturbs a neighbor. A permit is required.",
    keywords: [
      "aire", "acondicionador", "mini split", "minisplit", "condensadora", "ac", "air conditioning",
      "hvac", "unidad de aire",
    ],
  },
  {
    id: "arch-solar",
    source: "MANUAL",
    citation: "Manual §7 (Sistemas fotovoltaicos)",
    tags: ["architectural", "works-permit"],
    titleEs: "Placas solares y baterías",
    titleEn: "Solar panels and batteries",
    summaryEs:
      "Los sistemas solares/baterías deben cumplir el Código Eléctrico (NEC-2020), instalarse por perito autorizado y contar con aprobación del comité de arquitectura. En techos compartidos aplica la distribución 2/3–1/3 del espacio disponible entre los niveles.",
    summaryEn:
      "Solar/battery systems must meet the Electrical Code (NEC-2020), be installed by a licensed professional, and be approved by the architecture committee. On shared roofs the 2/3–1/3 split of available space between levels applies.",
    keywords: [
      "solar", "placas", "paneles", "fotovoltaico", "bateria", "baterias", "nec", "techo",
      "photovoltaic", "solar panels", "battery", "roof",
    ],
  },
  {
    id: "arch-generator",
    source: "MANUAL",
    citation: "Manual §7 (Generadores)",
    tags: ["architectural", "works-permit"],
    titleEs: "Generadores eléctricos",
    titleEn: "Electric generators",
    summaryEs:
      "Los generadores particulares no pueden exceder 7 kVA ni 75 decibeles, preferiblemente tipo inverter, ubicados donde no molesten al vecino y con escape canalizado. Requieren aprobación del comité de arquitectura.",
    summaryEn:
      "Private generators may not exceed 7 kVA or 75 decibels, preferably inverter type, located where they don't disturb neighbors and with piped exhaust. Architecture-committee approval is required.",
    keywords: [
      "generador", "generadores", "planta electrica", "inverter", "kva", "decibeles", "ruido",
      "generator", "generators", "power plant",
    ],
  },
  {
    id: "arch-finishes",
    source: "MANUAL",
    citation: "Manual §7 (Acabados exteriores)",
    tags: ["architectural", "works-permit"],
    titleEs: "Acabados exteriores: pintura, puertas, lámparas, barandas, losas",
    titleEn: "Exterior finishes: paint, doors, lamps, railings, pavers",
    summaryEs:
      "La pintura exterior no puede cambiarse de color. Puertas y manijas: acero inoxidable satinado, tipo palanca, superficie lisa. Lámparas exteriores cuadradas de 10\", abanicos de techo de 52\". Barandas solo se remueven con consentimiento unánime del edificio. Losas (pavers) color carbón con borde de cemento. Bloques de vidrio y ventanas según especificación aprobada.",
    summaryEn:
      "Exterior paint color cannot be changed. Doors and handles: satin stainless steel, lever type, smooth surface. Exterior lamps 10\" square, 52\" ceiling fans. Railings may only be removed with unanimous building consent. Pavers charcoal with a cement border. Glass block and windows per approved spec.",
    keywords: [
      "pintura", "puerta", "manija", "lampara", "baranda", "losas", "pavers", "ventana", "bloque de vidrio",
      "paint", "door", "handle", "lamp", "railing", "window", "glass block", "acabado", "abanico",
    ],
  },
  {
    id: "arch-satellite",
    source: "MANUAL",
    citation: "Manual §7 (Antenas / alarmas / plomería)",
    tags: ["architectural", "works-permit"],
    titleEs: "Antenas, alarmas y plomería exterior",
    titleEn: "Satellite dishes, alarms, and exterior plumbing",
    summaryEs:
      "Antenas parabólicas de 42\" o menos, en el techo y con consentimiento del vecino afectado. Las alarmas deben apagarse automáticamente en 5 minutos o menos. No se permiten duchas ni tomas de agua exteriores. Tanques de agua/jacuzzi requieren evaluación estructural y permiso.",
    summaryEn:
      "Satellite dishes 42\" or smaller, on the roof and with the affected neighbor's consent. Alarms must auto-silence in 5 minutes or less. Exterior showers and water taps are not allowed. Water tanks/jacuzzis require a structural review and a permit.",
    keywords: [
      "antena", "parabolica", "satelite", "alarma", "ducha", "tanque de agua", "jacuzzi", "cisterna",
      "satellite", "dish", "alarm", "shower", "water tank", "plumbing",
    ],
  },

  // ============ RENTAL / CESSION / TRANSFER ============
  {
    id: "rental-rules",
    source: "MANUAL",
    citation: "Manual §4",
    tags: ["rental", "access"],
    titleEs: "Alquiler / cesión de un apartamento",
    titleEn: "Renting / ceding an apartment",
    summaryEs:
      "El alquiler mínimo es de 3 meses (no se permite renta a corto plazo tipo hospedaje). El titular debe notificar por escrito a la administración con al menos 3 días de antelación, informando ocupantes y vehículos. No se entregan AVI/beeper/sticker si hay cuentas pendientes. Uso exclusivamente residencial.",
    summaryEn:
      "Minimum rental is 3 months (no short-term/lodging rentals). The owner must notify the administration in writing at least 3 days in advance, listing occupants and vehicles. No AVI/beeper/sticker is issued if there are outstanding balances. Residential use only.",
    keywords: [
      "alquiler", "renta", "arrendamiento", "inquilino", "cesion", "3 meses", "airbnb", "corto plazo",
      "rent", "rental", "lease", "tenant", "short term", "hospedaje",
    ],
  },
  {
    id: "transfer-fee",
    source: "REGLAMENTO",
    citation: "Escritura Matriz / Reglamento Art. X",
    tags: ["transfer-fee", "reserve"],
    titleEs: "Cuota de transferencia al vender (¼ de 1%)",
    titleEn: "Transfer fee on sale (¼ of 1%)",
    summaryEs:
      "En toda venta o traspaso de un apartamento se paga al fondo de reserva una cuota de transferencia equivalente a ¼ de 1% (0.25%) del precio de venta. Debe estar al día en mantenimiento para completar el traspaso.",
    summaryEn:
      "On every sale or transfer of an apartment, a transfer fee equal to ¼ of 1% (0.25%) of the sale price is paid to the reserve fund. The account must be current on maintenance to complete the transfer.",
    keywords: [
      "transferencia", "venta", "traspaso", "cuota de transferencia", "0.25", "fondo de reserva",
      "transfer fee", "sale", "closing", "estoppel",
    ],
  },

  // ============ ACCESS / PARKING / PETS / TRASH ============
  {
    id: "access-control",
    source: "MANUAL",
    citation: "Manual §1",
    tags: ["access"],
    titleEs: "Control de acceso, visitantes y stickers AVI",
    titleEn: "Access control, visitors, and AVI stickers",
    summaryEs:
      "El guardia registra toda entrada. Los visitantes se anuncian y autorizan por el titular. Los vehículos de residentes deben portar el sticker/AVI vigente; se emiten solo a titulares al día en sus cuentas. El beeper/control de portón es personal e intransferible.",
    summaryEn:
      "The guard logs every entry. Visitors are announced and authorized by the owner. Resident vehicles must carry a current sticker/AVI, issued only to owners current on their accounts. The gate beeper/remote is personal and non-transferable.",
    keywords: [
      "acceso", "guardia", "visitante", "avi", "sticker", "beeper", "porton", "control",
      "access", "guard", "visitor", "gate", "entry",
    ],
  },
  {
    id: "parking-rules",
    source: "MANUAL",
    citation: "Manual §3",
    tags: ["parking"],
    titleEs: "Estacionamiento",
    titleEn: "Parking",
    summaryEs:
      "Cada apartamento usa solo su espacio asignado; las visitas usan el estacionamiento de visitantes. No se permiten botes, remolques ni vehículos comerciales grandes. Se debe estacionar en reversa (requisito del código de bomberos). El bloqueo de espacios ajenos o áreas comunes conlleva multa y remolque.",
    summaryEn:
      "Each apartment uses only its assigned space; guests use visitor parking. Boats, trailers, and large commercial vehicles are not allowed. Vehicles must back into spaces (fire-code requirement). Blocking others' spaces or common areas is subject to a fine and towing.",
    keywords: [
      "estacionamiento", "parking", "espacio", "bote", "remolque", "reversa", "visitante", "grua",
      "park", "space", "boat", "trailer", "tow", "assigned",
    ],
  },
  {
    id: "pet-rules",
    source: "MANUAL",
    citation: "Manual §5",
    tags: ["pet"],
    titleEs: "Mascotas",
    titleEn: "Pets",
    summaryEs:
      "Se permiten perros y gatos. En áreas comunes deben ir con correa y el dueño debe recoger los desperdicios inmediatamente. No se permite dejar mascotas sueltas ni que causen ruido o molestias reiteradas. El incumplimiento conlleva multa.",
    summaryEn:
      "Dogs and cats are allowed. In common areas they must be leashed and the owner must pick up waste immediately. Pets may not roam free or cause repeated noise/nuisance. Noncompliance is subject to a fine.",
    keywords: [
      "mascota", "mascotas", "perro", "gato", "correa", "animal", "pet", "pets", "dog", "cat", "leash",
    ],
  },
  {
    id: "facility-use",
    source: "MANUAL",
    citation: "Manual §2",
    tags: ["access", "trash"],
    titleEs: "Uso de áreas comunes, mudanzas y actividades",
    titleEn: "Common-area use, moves, and events",
    summaryEs:
      "Las actividades con 15 o más invitados requieren notificar a la administración con 2 días de antelación. Las mudanzas requieren coordinación previa y un depósito de $500 que responde por daños. La basura se deposita embolsada en el área y horario designados; no se dejan enseres ni escombros.",
    summaryEn:
      "Events with 15 or more guests require 2 days' advance notice to the administration. Moves require prior coordination and a $500 deposit covering damage. Trash is bagged and placed in the designated area/time; no appliances or debris are left behind.",
    keywords: [
      "mudanza", "actividad", "fiesta", "invitados", "deposito", "500", "areas comunes",
      "move", "event", "party", "guests", "common areas", "deposit",
    ],
  },

  // ============ GOVERNANCE: MEETINGS / QUORUM / VOTES ============
  {
    id: "annual-assembly",
    source: "REGLAMENTO",
    citation: "Reglamento Art. II",
    tags: ["meeting", "quorum"],
    titleEs: "Asamblea anual del Consejo de Titulares",
    titleEn: "Annual assembly of the Consejo de Titulares",
    summaryEs:
      "La asamblea ordinaria anual se celebra dentro de los 90 a 120 días siguientes al cierre del año fiscal (1 ago–31 jul), es decir aproximadamente octubre–noviembre. En ella se aprueban el informe, el presupuesto y la Junta.",
    summaryEn:
      "The annual ordinary assembly is held within 90 to 120 days after the fiscal-year close (Aug 1–Jul 31), i.e. roughly October–November. It approves the report, the budget, and the board.",
    keywords: [
      "asamblea", "anual", "ordinaria", "consejo de titulares", "reunion anual",
      "assembly", "annual", "meeting", "consejo",
    ],
  },
  {
    id: "convocatoria-notice",
    source: "REGLAMENTO",
    citation: "Reglamento Art. II",
    tags: ["meeting", "quorum"],
    titleEs: "Convocatoria: plazos de notificación",
    titleEn: "Convocatoria: notice periods",
    summaryEs:
      "La convocatoria de la asamblea ordinaria debe notificarse con al menos 15 días de antelación; la extraordinaria, con un mínimo de 5 días. Debe incluir fecha, hora, lugar y agenda, y entregarse por los medios que dispone el Reglamento.",
    summaryEn:
      "Notice of the ordinary assembly must be given at least 15 days in advance; the extraordinary assembly, a minimum of 5 days. It must include date, time, place, and agenda, delivered by the means the Reglamento provides.",
    keywords: [
      "convocatoria", "aviso", "notificacion", "15 dias", "5 dias", "agenda", "citacion",
      "notice", "convocation", "days", "call to meeting",
    ],
  },
  {
    id: "quorum-thresholds",
    source: "REGLAMENTO",
    citation: "Reglamento Art. II",
    tags: ["quorum", "vote"],
    titleEs: "Quórum y mayorías para votar",
    titleEn: "Quorum and voting majorities",
    summaryEs:
      "Los acuerdos ordinarios se toman por mayoría (mitad más uno) de los votos presentes. Ciertos actos (obras extraordinarias, cambios al régimen, gastos mayores) requieren 2/3 de todos los titulares que representen 2/3 de la participación. Los titulares ausentes deben ser notificados y tienen 30 días para objetar.",
    summaryEn:
      "Ordinary decisions are made by a majority (half plus one) of votes present. Certain acts (extraordinary works, changes to the regime, major expenditures) require 2/3 of all owners representing 2/3 of participation. Absent owners must be notified and have 30 days to object.",
    keywords: [
      "quorum", "mayoria", "votacion", "voto", "dos tercios", "2/3", "participacion", "30 dias",
      "vote", "majority", "supermajority", "two thirds", "ausente",
    ],
  },
  {
    id: "board-composition",
    source: "REGLAMENTO",
    citation: "Reglamento Art. III",
    tags: ["meeting", "vote"],
    titleEs: "Composición y términos de la Junta de Directores",
    titleEn: "Board composition and terms",
    summaryEs:
      "La Junta se compone de Presidente, Secretario, Tesorero y hasta 6 vocales. Los miembros no pueden servir más de 3 términos consecutivos. La Junta ejecuta los acuerdos del Consejo y administra el condominio conforme al Reglamento.",
    summaryEn:
      "The board consists of a President, Secretary, Treasurer, and up to 6 members. Members may not serve more than 3 consecutive terms. The board executes the Consejo's decisions and administers the condominium per the Reglamento.",
    keywords: [
      "junta", "directiva", "presidente", "secretario", "tesorero", "vocal", "terminos",
      "board", "directors", "president", "secretary", "treasurer", "term",
    ],
  },

  // ============ RESERVE FUND ============
  {
    id: "reserve-fund",
    source: "REGLAMENTO",
    citation: "Reglamento Art. X",
    tags: ["reserve"],
    titleEs: "Fondo de reserva",
    titleEn: "Reserve fund",
    summaryEs:
      "El condominio mantiene un fondo de reserva con aportación mínima del 5% del presupuesto anual, que puede suspenderse cuando el fondo alcanza el 2% del costo de reproducción del inmueble. Se nutre además de la cuota de transferencia (¼ de 1%).",
    summaryEn:
      "The condominium keeps a reserve fund with a minimum contribution of 5% of the annual budget, which may be suspended when the fund reaches 2% of the building's reproduction cost. It is also funded by the transfer fee (¼ of 1%).",
    keywords: [
      "fondo de reserva", "reserva", "5%", "2%", "presupuesto", "reserve", "reserve fund", "budget",
    ],
  },
  {
    id: "participation-coefficient",
    source: "ESCRITURA",
    citation: "Escritura Matriz, cláusula Séptima",
    tags: ["vote", "reserve"],
    titleEs: "Coeficiente de participación de cada apartamento",
    titleEn: "Participation coefficient of each apartment",
    summaryEs:
      "Cada apartamento tiene un porcentaje de participación en los elementos comunes fijado en la Escritura Matriz (según su área). Este coeficiente es la base legal para el peso del voto en la asamblea y para la distribución de gastos comunes. Los 108 apartamentos (edificios E–R) suman 100%.",
    summaryEn:
      "Each apartment has a participation percentage in the common elements set in the master deed (by its area). This coefficient is the legal basis for the weight of the vote in the assembly and for the allocation of common expenses. The 108 apartments (buildings E–R) sum to 100%.",
    keywords: [
      "coeficiente", "participacion", "porcentaje", "elementos comunes", "escritura",
      "coefficient", "participation", "percentage", "common elements", "share",
    ],
  },

  // ============ EMERGENCY / RATIONING ============
  {
    id: "emergency-hurricane",
    source: "EMERGENCIA",
    citation: "Plan de Emergencias (Huracanes)",
    tags: ["emergency"],
    titleEs: "Protocolo de huracán (Condiciones IV–I)",
    titleEn: "Hurricane protocol (Conditions IV–I)",
    summaryEs:
      "El Plan define acciones por condición de huracán: Condición IV (vigilancia/preparativos), III, II y I (tormenta inminente, aseguramiento total y refugio). Incluye asegurar objetos sueltos, retirar toldos, y seguir las instrucciones del comité de emergencias. El comité activa el plan y coordina con el municipio.",
    summaryEn:
      "The Plan defines actions by hurricane condition: Condition IV (watch/preparation), III, II, and I (imminent storm, full securing, and shelter). It includes securing loose objects, retracting awnings, and following the emergency committee's instructions. The committee activates the plan and coordinates with the municipality.",
    keywords: [
      "huracan", "tormenta", "emergencia", "condicion", "desastre", "preparativo",
      "hurricane", "storm", "emergency", "disaster", "condition", "shelter",
    ],
  },
  {
    id: "emergency-tsunami",
    source: "EMERGENCIA",
    citation: "Plan de Emergencias (Tsunami/Terremoto)",
    tags: ["emergency"],
    titleEs: "Tsunami y terremoto: zona de evacuación",
    titleEn: "Tsunami and earthquake: evacuation zone",
    summaryEs:
      "El condominio está en zona de desalojo por tsunami. Ante un terremoto fuerte o el retroceso notable del mar, evacúe de inmediato a terreno alto sin esperar aviso oficial. El Plan detalla rutas y puntos de encuentro.",
    summaryEn:
      "The condominium is in a tsunami evacuation zone. After a strong earthquake or a notable receding of the sea, evacuate immediately to high ground without waiting for an official alert. The Plan details routes and meeting points.",
    keywords: [
      "tsunami", "terremoto", "sismo", "evacuacion", "desalojo", "maremoto",
      "earthquake", "evacuation", "high ground", "quake",
    ],
  },
  {
    id: "emergency-registry",
    source: "EMERGENCIA",
    citation: "Plan de Emergencias (Registro de asistencia)",
    tags: ["emergency"],
    titleEs: "Registro de residentes que requieren asistencia",
    titleEn: "Registry of residents needing assistance",
    summaryEs:
      "El Plan mantiene un registro de residentes que necesitan ayuda durante una emergencia (movilidad reducida, equipo médico, adultos mayores). La administración debe mantenerlo actualizado y compartirlo con el comité de emergencias y los rescatistas.",
    summaryEn:
      "The Plan keeps a registry of residents who need help during an emergency (limited mobility, medical equipment, elderly). The administration must keep it current and share it with the emergency committee and responders.",
    keywords: [
      "registro", "asistencia", "movilidad", "medico", "adulto mayor", "necesidades especiales",
      "registry", "assistance", "mobility", "medical", "special needs",
    ],
  },
  {
    id: "rationing-plan",
    source: "RACIONAMIENTO",
    citation: "Plan de Racionamiento de Agua y Energía",
    tags: ["rationing", "emergency"],
    titleEs: "Racionamiento de agua y energía",
    titleEn: "Water and power rationing",
    summaryEs:
      "El condominio no tiene cisterna de agua potable, por lo que en racionamiento cada residente debe almacenar su propia agua; se suspende el riego y la limpieza de áreas comunes. El generador de 20 kW alimenta solo áreas comunes (pasillos, escaleras, estacionamiento), típicamente de 7:00 p.m. a 7:00 a.m.",
    summaryEn:
      "The condominium has no potable-water cistern, so during rationing each resident must store their own water; irrigation and common-area cleaning are suspended. The 20 kW generator powers only common areas (hallways, stairs, parking), typically 7:00 p.m. to 7:00 a.m.",
    keywords: [
      "racionamiento", "agua", "energia", "generador", "cisterna", "apagon", "luz",
      "rationing", "water", "power", "generator", "outage", "blackout",
    ],
  },

  // ============ USE RESTRICTIONS ============
  {
    id: "use-restrictions",
    source: "ESCRITURA",
    citation: "Escritura Matriz (Restricciones de uso)",
    tags: ["rental", "access"],
    titleEs: "Restricciones de uso del apartamento",
    titleEn: "Apartment use restrictions",
    summaryEs:
      "Los apartamentos son exclusivamente para vivienda; no se permite comercio, hospedaje ni actividad que altere la tranquilidad. No se permiten rótulos ni anuncios visibles al exterior, ni cambios estructurales que afecten elementos comunes o la seguridad del edificio.",
    summaryEn:
      "Apartments are for residential use only; commerce, lodging, or activity that disturbs the peace is not allowed. No signs or advertising visible from outside, and no structural changes affecting common elements or building safety.",
    keywords: [
      "uso", "residencial", "comercio", "negocio", "rotulo", "anuncio", "restriccion",
      "use", "residential", "commercial", "business", "sign", "restriction",
    ],
  },
];

// ============ Machine-usable constants distilled from the documents ============
// Surfaced as guidance today; ready for automated enforcement in a later phase.

/** Progressive fine schedule for rule violations (Reglamento Art. VI Sec. 2(T)). */
export const FINE_LADDER = [
  { step: 1, amount: 0, es: "Aviso escrito", en: "Written notice" },
  { step: 2, amount: 50, es: "Multa $50", en: "$50 fine" },
  { step: 3, amount: 100, es: "Multa $100", en: "$100 fine" },
  { step: 4, amount: 100, per: "day", es: "$100 por día que persista", en: "$100 per day it persists" },
] as const;

/** Permitted construction hours (Manual §6.6). 24-hour clock. */
export const CONSTRUCTION_HOURS = {
  weekday: { start: "08:00", end: "17:00" },
  saturday: { start: "08:00", end: "17:00", note: "Solo trabajo liviano / light work only" },
  sundayHolidays: null, // prohibited
} as const;

/** Puerto Rico public holidays when construction is prohibited. */
export const HOLIDAYS_PR = [
  "Año Nuevo", "Día de Reyes", "Día de Martin Luther King", "Día de los Presidentes",
  "Día de la Abolición de la Esclavitud", "Viernes Santo", "Día de la Recordación",
  "Día de la Independencia (EE.UU.)", "Constitución de PR", "Día del Trabajo",
  "Día de la Raza", "Día del Veterano", "Día de Acción de Gracias", "Navidad",
] as const;

export const RENTAL_MIN_MONTHS = 3;
export const RENTAL_NOTICE_DAYS = 3;
export const TRANSFER_FEE_RATE = 0.0025; // ¼ of 1% of the sale price (to the reserve fund)
export const WORKS_PERMIT_BOND = 1000;
export const MOVE_DEPOSIT = 500;
export const CONVOCATORIA_NOTICE_DAYS = { annual: 15, extraordinary: 5 } as const;
export const RESERVE_MIN_BUDGET_PCT = 0.05; // 5% of the annual budget
export const RECONSIDERATION_DAYS = 20;

/** Rules tagged with the given tag, preserving catalog order. */
export function rulesFor(tag: RuleTag): GovernanceRule[] {
  return GOVERNANCE_RULES.filter((r) => r.tags.includes(tag));
}

/** Rules matching any of the given tags (union), de-duplicated, in catalog order. */
export function rulesForAny(tags: RuleTag[]): GovernanceRule[] {
  const set = new Set(tags);
  return GOVERNANCE_RULES.filter((r) => r.tags.some((t) => set.has(t)));
}
